import {ChangeDetectorRef, Component, ViewChild, OnInit, OnDestroy} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import * as _ from 'lodash';

import { ECOMMERCE_PANEL } from '../../../product-panel/product-panel-types';
import * as utils from 'utils/utils';
import { Package } from '../../models/package.model';
import { ExtendedInformation } from '../../models/extended-information.model';
import { BasePackagingComponent } from './_base-packaging.component';
import { SquareArrowDropdownComponent } from 'shared/dropdown/square-arrow-dropdown/square-arrow-dropdown.component';
import { ProductPanelService } from '../../product-panel.service';
import { segmentsData } from '../../../product-details/segments-data-stub';
import { BrandService } from 'shared/brand/brand.service';
import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import { SearchService } from 'shared/search/search.service';


@Component({
  moduleId: module.id,
  selector: 'packaging-segment',
  templateUrl: 'packaging.component.html',
  styleUrls: ['packaging.component.scss'],
})
export class PackagingComponent extends BasePackagingComponent implements OnInit, OnDestroy {

  packagePathName: string = 'packages';
  headers: Array<string> = [
    'title', 'orderable', 'quantity'
  ];
  headersData: Array<any>;
  itemDetailsData: any = {};
  packagesData: Array<any>;
  extendedInformation: ExtendedInformation;
  extendedInformationData: Array<any>;
  extendedInformationDetails: Array<any>;
  loaded: boolean = true;
  units: Array<any> = [
    {
      id: 'metric',
      title: 'Metric'
    },
    {
      id: 'standard',
      title: 'Standard'
    }
  ];
  segmentName: string = 'packages';
  panelTypeFields: any = {
    [ECOMMERCE_PANEL]: [
      'EA.title', 'EA.orderable', 'EA.quantity',
      'EA.dimensions.length', 'EA.dimensions.width',
      'EA.dimensions.height', 'EA.dimensions.uom',
      'EA.weights.weight', 'EA.weights.uom',
      'EA.package_level_GTIN'
    ],
  };

  packagesFormGroup: UntypedFormGroup;

  @ViewChild(SquareArrowDropdownComponent, {static: false}) unitsDropdown: SquareArrowDropdownComponent | void;

  constructor(
    protected productPanelService: ProductPanelService,
    protected brandService: BrandService,
    protected itemDetailService: ItemDetailService,
    protected searchService: SearchService,
    protected route: ActivatedRoute,
    protected ref: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
  ) {
    super(productPanelService, brandService, itemDetailService, searchService, route, ref);
  }

  ngOnInit() {
    this.packagesFormGroup = this.fb.group({
      packages: this.fb.array([]),
      extendedInformation: this.fb.group({})
    });
    this.subscribeToItemDetail();
  }

  ngOnDestroy() {
    this.packagesData = [];
    super.ngOnDestroy();
  }

  get packages(): UntypedFormArray {
    return this.packagesFormGroup.get('packages') as UntypedFormArray;
  }

  get extendedInformationFormGroup(): UntypedFormGroup {
    return this.packagesFormGroup.get('extendedInformation') as UntypedFormGroup;
  }

  get segmentForm(): UntypedFormGroup {
    return this.packagesFormGroup;
  }

  initValidators(validation: any): any[] {
    return utils.initValidators(validation, this);
  }

  addTableHeaders(data: Array<any>): void {
    this.headersData = _.map(this.headers, (name: string) => {
      return _.find(data, (item: any) => {
        return this.matchItemName(item.item_name, '.' + name);
      });
    });
  }

  // replace parent method:
  setBulkData() {
    const data = this.itemDetailService.prepareItemDetailPackages(
      this.getBulkSegmentData(this.segmentName)
    );
    this.setData(data);
    this.loaded = true;
  }

  // replace parent method:
  handleItemDetail(itemDetail: any) {
    this.setData(itemDetail);
    this.loaded = true;
  }

  getPackageFields(name: string, data: any[]): any {
    return data.filter((item: any) => this.matchItemName(item.item_name + '.', name));
  }

  prepareData(_segmentsData: any, itemDetailsData: any): void {
    let packagesSegments = this.getPanelSegmentsData(_segmentsData.packages);
    this.itemDetailsData = itemDetailsData;
    this.addTableHeaders(packagesSegments);
    this.extendedInformationDetails = itemDetailsData.extendedInformation;
    this.extendedInformationData = _segmentsData.extendedInformation;
    if (this.bulkEdit) {
      packagesSegments = this.getSegmentsWithPlaceholder(_segmentsData.packages);
      this.extendedInformationData = this.getSegmentsWithPlaceholder(_segmentsData.extendedInformation);
    }
    const packages = (this.panelType === ECOMMERCE_PANEL) ? {'EA': this.itemDetailsData.packages.EA} : this.itemDetailsData.packages;
    this.packagesData = _.map(packages, (value: any, keyName: any) => {
      const data: any[] = this.getPackageFields(keyName, packagesSegments);
      return {
        packageUom: keyName,
        headersData: this.filterData(data, this.headers),
        data: data,
        itemDetailsData: value
      };
    });
  }

  setUoms(data: any) {
    if (!data) { return this.unitsDropdown instanceof SquareArrowDropdownComponent ? this.unitsDropdown.setActive(null, true) : null; }
    const uom = _.get(_.values(data)[0], 'dimensions.uom', '');
    if (uom) {
      if (this.unitsDropdown instanceof SquareArrowDropdownComponent) {
        this.unitsDropdown.setActive(uom === 'IN' ? 'standard' : 'metric', true);
      }
    } else {
      if (this.unitsDropdown instanceof SquareArrowDropdownComponent) {
        this.unitsDropdown.setActive('standard', true);
      }
      this.setPackageFormGroupUoms('standard');
    }
  }

  setData(itemDetail: any = {}) {
    this.prepareData(segmentsData, itemDetail);
    this.setPackagesFormArray(this.packagesData);
    this.setUoms(itemDetail.packages);
    this.setextendedInformationFormGroup(segmentsData.extendedInformation);
  }

  setPackagesFormArray(data: Array<any>) {
    const packagesFGs = data.map((item: any) => {
      const packageModel = new Package();
      _.each(item.data, (_item: any) => {
        let value = _.get(this.itemDetailsData.packages, _item.item_name, '');
        if (_item.field_map === 'weightUom' && !value) {
          if (this.unitsDropdown instanceof SquareArrowDropdownComponent) {
            value = this.getWeightUom(this.unitsDropdown.selectedItem.id);
          }
        }
        if (_item.field_map === 'dimensionsUom' && !value) {
          if (this.unitsDropdown instanceof SquareArrowDropdownComponent) {
            value = this.getLengthUom(this.unitsDropdown.selectedItem.id);
          }
        }
        const validators = this.getItemValidators(_item.validation, packageModel );
        _.set(packageModel, _item.field_map, new UntypedFormControl(value, validators));
      });
      _.set(packageModel, 'packageUom', new UntypedFormControl(item.packageUom));
      return this.fb.group(packageModel);
    });
    const packagesFormArray = this.fb.array(packagesFGs);
    this.packagesFormGroup.setControl('packages', packagesFormArray);
  }

  setextendedInformationFormGroup(extendedInformationData: Array<any>) {
    this.extendedInformation = new ExtendedInformation();
    _.each(extendedInformationData, (obj: any) => {
      if (!obj.field_map) { return; }
      const value = _.get(this.extendedInformationDetails, obj.item_name, '');
      const validators = this.getItemValidators(obj.validation, this);
      const control = new UntypedFormControl(value, validators);
      _.set(this.extendedInformation, obj.field_map, control);
    });
    const formGroup = this.fb.group(this.extendedInformation);
    this.packagesFormGroup.setControl('extendedInformation', formGroup);
  }

  save(): Observable<any> {
    const submitData: any = {
      packages: this.packages.value,
      extendedInformation: this.extendedInformationFormGroup.value
    };
    if (this.bulkEdit) {
      return this.putDataBulk(this.segmentName, submitData, this.prepareBulkData(submitData));
    } else {
      const { id } = this.route.snapshot.params;
      return this.putData(id, this.segmentName, submitData);
    }
  }

  showHazMatSegment(item: any): boolean {
    if (this.bulkEdit) { return true; }
    return item.itemDetailsData.hazardous_materials && item.itemDetailsData.hazardous_materials.length;
  }

  onUnitsChange(event: any) {
    if (!event) { return; }
    this.setPackageFormGroupUoms(event.id);
    this.packagesFormGroup.markAsDirty();
  }

  private setPackageFormGroupUoms(system: string) {
    const lengthUOM: string = this.getLengthUom(system);
    const weightUOM: string = this.getWeightUom(system);
    this.packagesFormGroup.controls.packages.patchValue([
      {dimensionsUom: lengthUOM, weightUom: weightUOM},
      {dimensionsUom: lengthUOM, weightUom: weightUOM},
      {dimensionsUom: lengthUOM, weightUom: weightUOM},
      {dimensionsUom: lengthUOM, weightUom: weightUOM},
      {dimensionsUom: lengthUOM, weightUom: weightUOM},
    ]);
  }

  private prepareBulkData(data: any): any {
    if (!data) { return {}; }
    const result = {};
    // prepare packages
    const packages: any = _.groupBy(data.packages, 'packageUom');
    _.each(packages, (value: any, key: any) => {
      packages[key] = value[0];
    });
    _.each(segmentsData.packages, (item: any) => {
      _.set(
        result,
        `packages.${item.item_name}`,
        _.get(packages, `${item.item_name.split('.')[0]}.${item.field_map}`, '')
      );
    });
    // prepare extended info
    _.each(data.extendedInformation, (value, key: string) => {
      _.set(result, `extendedInformation.${key.toUpperCase()}.EN`, value);
    });
    return result;
  }

  private getLengthUom(id: string): string {
    return id === 'standard' ? 'IN' : 'CM';
  }

  private getWeightUom(id: string): string {
    return id === 'standard' ? 'PG' : 'GT';
  }
}
